import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import { loadUser, fetchUserForm, updateUser } from "../../api/adminApi";
import { toggleUserStatus, changeUserPassword } from "../../api/settingApi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faEdit, faLock, faLockOpen, faPlus, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

Modal.setAppElement('#root');

const ManageUser = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [roleList, setRoleList] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    email: "",
    role: ""
  });
  const [editErrors, setEditErrors] = useState({});

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const getStatusBadge = (status) => {
    if (status === "Active") {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-800 font-semibold">
          Active
        </span>
      );
    } else if (status === "Inactive") {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-800 font-semibold">
          Inactive
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700 font-semibold">
          Unknown
        </span>
      );
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await loadUser();
      setList(response.data);
    } catch (error) {
      console.error("Error loading admins", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (adminId, name, currentStatus) => {
    const action = currentStatus === "Active" ? "lock" : "unlock";
    const confirmResult = await Swal.fire({
      title: `Are you sure you want to ${action} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: currentStatus === "Active" ? "#e53935" : "#43a047", // red/green
    });

    if (!confirmResult.isConfirmed) return;

    try {
      await toggleUserStatus(adminId);
      toast.success(`User ${name} has been ${action}ed successfully.`, {
        position: "top-right",
        autoClose: 3000,
      });

      setList(prevList => prevList.map(admin =>
        admin.id === adminId
          ? { ...admin, status: admin.status === 'Active' ? 'Inactive' : 'Active' }
          : admin
      ));
    } catch (error) {
      console.error("Failed to toggle status", error);
    }
  };

  const handleEditUser = async (user) => {
    try {
      setSelectedAdmin(user);
      const data = await fetchUserForm(user.id);
      setEditForm(data.registrationDTO);
      setRoleList(data.roleList);
      setEditModalOpen(true);
    } catch (err) {
      toast.error("Failed to load user data.");
    }
    setEditErrors({});
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedAdmin?.id) {
      setError("User ID not set.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,50}$/;
    if (!regex.test(newPassword)) {
      setError("Password must be between 8 and 50 characters long, contain at least one uppercase letter, one lowercase letter, and one number.");
      return;
    }

    try {
      await changeUserPassword({
        userId: selectedAdmin.id,
        passwordString: newPassword
      });
      setSuccess("Password changed successfully.");
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      setError("Failed to change password.");
    }
  };

  const validateEditForm = () => {
    const errors = {};

    if (!/^[A-Za-z\s]{2,50}$/.test(editForm.name)) {
      errors.name = "Name must be 2–50 letters only.";
    }


    if (!/^\S+@\S+\.\S+$/.test(editForm.email)) {
      errors.email = "Invalid email format.";
    }

    if (!editForm.role) {
      errors.role = "Role is required.";
    }

    return errors;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    if (
      selectedAdmin.name === editForm.name.trim() &&
      selectedAdmin.email === editForm.email.trim() &&
      selectedAdmin.role === editForm.role
    ) {
      toast.info("No changes made.");
      return;
    }

    try {
      const payload = {
        id: selectedAdmin.id,
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
      };

      await updateUser(payload);
      toast.success("User updated successfully!");
      setEditModalOpen(false);
      fetchUsers(); // refresh
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        "Failed to update user. Please try again."
      );
    }
  };

  const openModal = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSuccess("");
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedAdmin(null);
    setError("");
    setSuccess("");
    setNewPassword("");
    setConfirmPassword("");
  };

  if (loading) {
    return <div className="text-center py-8 text-blue-600 font-semibold">Loading...</div>;
  }

  return (<>
    <div className="bg-blue-50 p-8 min-h-screen">
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-blue-800">Backend Users</h2>
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 shadow-md flex items-center space-x-2"
            onClick={() => navigate('/admin/add-user')}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Add User</span>
          </button>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Username</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Organization</th>
                <th className="px-6 py-3 text-left text-sm font-bold text-blue-700 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-sm font-bold text-blue-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {list.map((e) => (
                <tr key={e.id} className="hover:bg-blue-50 transition-all">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{e.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{e.organization}</td>
                  <td className="px-6 py-4 text-sm">{getStatusBadge(e.status)}</td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button
                      className="border px-2 py-1 rounded text-red-600 border-red-600 hover:text-white hover:bg-red-600 transition"
                      title={e.status === "Active" ? "Lock User" : "Unlock User"}
                      onClick={() => handleToggleStatus(e.id, e.name, e.status)}
                    >
                      <FontAwesomeIcon icon={e.status === "Active" ? faLockOpen : faLock} />
                    </button>

                    <button
                      className="border px-2 py-1 rounded text-green-600 border-green-600 hover:text-white hover:bg-green-600 transition"
                      title="Update"
                      onClick={() => handleEditUser(e)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>

                    <button
                      className="border px-2 py-1 rounded text-blue-600 border-blue-600 hover:text-white hover:bg-blue-600 transition"
                      title="Change Password"
                      onClick={() => openModal(e)}
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-gray-500 py-6">
                    No admin users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4 text-blue-700">Update User</h2>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditInputChange}
                  className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${editErrors.name ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-blue-400"
                    }`}
                />
                {editErrors.name && <p className="text-red-500 text-sm">{editErrors.name}</p>}
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditInputChange}
                  className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${editErrors.email ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-blue-400"
                    }`}
                />
                {editErrors.email && <p className="text-red-500 text-sm">{editErrors.email}</p>}
              </div>

              {/* Role Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  name="role"
                  value={editForm.role}
                  onChange={handleEditInputChange}
                  className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${editErrors.role ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-blue-400"
                    }`}
                >
                  <option value="">Select Role</option>
                  {roleList.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                {editErrors.role && <p className="text-red-500 text-sm">{editErrors.role}</p>}
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded text-white bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Change Password Modal"
        className="bg-white p-6 max-w-md mx-auto mt-20 rounded-lg shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start"
      >
        <h2 className="text-xl font-semibold text-blue-600 mb-4">Change Password for {selectedAdmin?.name}</h2>

        {error && <div className="bg-red-500 text-white px-4 py-2 rounded mb-3">{error}</div>}
        {success && <div className="bg-green-500 text-white px-4 py-2 rounded mb-3">{success}</div>}

        <form onSubmit={handlePasswordChange}>
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type={showNewPassword ? "text" : "password"}
              className="w-full border border-gray-300 px-3 py-2 rounded pr-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Enter new password"
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full border border-gray-300 px-3 py-2 rounded pr-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Save
            </button>
          </div>
        </form>
      </Modal>
    </div>
  </>
  );

};

export default ManageUser;
